/**
 * AboutUs — the founder story. Replaces the /about-us ComingSoonPage stub
 * (contest brief, 2026-07-11).
 *
 * Source of truth: press_kit/SONSPEAK_ONE_PAGER.md — a single dad of a
 * nonverbal autistic son, an iPad, $200–$300 apps he couldn't afford,
 * and a board built with AI as co-builder. The founder is deliberately
 * unnamed (contact-by-email operation; the focus stays on the product
 * and the families it serves). The pull-quote is kept VERBATIM from the
 * press kit — existing traction copy may be kept verbatim or dropped,
 * never inflated.
 *
 * Warm, short, true. No invented numbers, partners, or credentials.
 */
import { Link } from "react-router-dom";
import { Heart, Lock, Hammer, ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/seo";

export default function AboutUs() {
  useSEO({
    title: "About us",
    description:
      "SonSpeak was built by a parent for his nonverbal son — because the " +
      "leading AAC apps cost $200–$300 and insurance devices take years. " +
      "Privacy is the product.",
    path: "/about-us",
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="about-us-page">
      {/* Story hero */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-10">
        <p className="display text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] mb-3">
          Our story
        </p>
        <h1 className="display text-3xl sm:text-4xl font-extrabold leading-tight mb-6">
          For my son — and every family like ours.
        </h1>
        <div className="space-y-4 text-base sm:text-lg leading-relaxed text-[color:var(--ink)]/85">
          <p>
            If you're here, you probably know the feeling. Your child has so
            much to say, and the tools that could help either cost more than a
            month of rent or sit behind months of insurance paperwork.
          </p>
          <p>
            I'm a single dad. My son is nonverbal and autistic. I bought him an
            iPad so he could have a voice — and then found out the AAC apps
            run <strong>$200–$300</strong>, the dedicated devices insurance is
            &ldquo;supposed&rdquo; to cover can take years of appeals, and the
            $36–$60 picture boards can't grow as he grows.
          </p>
          <p>
            So I built the board he actually needed — real photos of the people
            he loves, my own voice on the tiles, everything living right on his
            device. It worked. He started reaching for it.
          </p>
          <p>
            Then I kept building it out, for you — because I don't think any
            family should have to wait or pay a fortune just to hear their kid
            ask for a snack, or say &ldquo;I love you.&rdquo;
          </p>
        </div>
      </section>

      {/* Pull quote — verbatim from the press kit */}
      <section className="border-y-[3px] border-[color:var(--ink)] bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <blockquote
            className="display text-xl sm:text-2xl font-extrabold leading-snug text-center"
            data-testid="about-us-quote"
          >
            &ldquo;Communication is a human right. No family should have to
            wait on insurance — or pay hundreds — for their child to have
            one.&rdquo;
          </blockquote>
        </div>
      </section>

      {/* What we believe */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <h2 className="display text-2xl sm:text-3xl font-extrabold text-center mb-10">
          What it's built around
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="brutal-card bg-white p-6">
            <div className="w-11 h-11 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center mb-4">
              <Lock size={20} aria-hidden />
            </div>
            <h3 className="display text-lg font-extrabold mb-2">
              Privacy is the product
            </h3>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              &ldquo;Zero remotely readable identifying child content.&rdquo;
              Your child's photos, voice, and words live encrypted on your
              device — not in anyone's cloud, including ours.
            </p>
          </div>
          <div className="brutal-card bg-white p-6">
            <div className="w-11 h-11 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center mb-4">
              <Heart size={20} aria-hidden />
            </div>
            <h3 className="display text-lg font-extrabold mb-2">
              The family's soul in it
            </h3>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              Grandma's real face on the tile. Your real voice saying the
              word. A communication board should sound like home, not like a
              call center.
            </p>
          </div>
          <div className="brutal-card bg-white p-6">
            <div className="w-11 h-11 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center mb-4">
              <Hammer size={20} aria-hidden />
            </div>
            <h3 className="display text-lg font-extrabold mb-2">
              Complete without extras
            </h3>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              Building boards by hand is the complete product — AI features
              are a convenience you can add, never a toll you must pay. The
              child's screen has zero AI, by architecture.
            </p>
          </div>
        </div>
      </section>

      {/* Who we are, honestly */}
      <section className="border-t-[3px] border-[color:var(--ink)] bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-14 text-center">
          <h2 className="display text-2xl font-extrabold mb-4">
            It's just me, honestly
          </h2>
          <p className="text-sm sm:text-base text-[color:var(--ink)]/80 leading-relaxed max-w-2xl mx-auto mb-3">
            SonSpeak is a small, independent project — one parent building for
            families like mine, and I read every message myself. I keep my name
            off the site on purpose: this isn't about me, it's about your child
            having a voice.
          </p>
          <p className="text-sm sm:text-base text-[color:var(--ink)]/80 leading-relaxed max-w-2xl mx-auto mb-8">
            It's early access, and I'll always be straight with you about that.
            What you see here is what really works today — when something's
            still being built, I'll say so. No fake screens, no empty promises.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="pill-btn primary text-sm" data-testid="about-us-contact-cta">
              Reach out anytime <ArrowRight size={14} aria-hidden />
            </Link>
            <Link to="/privacy-promise" className="pill-btn text-sm" data-testid="about-us-privacy-cta">
              Read the privacy promise
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
